import { useEffect, useState } from 'react';
import { Row, Col, Tabs, Breadcrumb, Spin } from 'antd';
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from 'prop-types';
import Box from '../../../components/utility/box';
import { DvIntlMessages } from '../../../components/utility/dvGeneralUtilities';
import IntlMessages from "../../../components/utility/intlMessages";
import systemActions from '../../../redux/systemInfos/actions';
import clinicsStyles from '../clinics.module.scss'
import ClinicDetailsGeneral from './clinicDetails.general';
import ClinicDetailsUsers from './clinicDetails.users';
import ClinicDetailsHealthRecords from './clinicDetails.hr';
import ClinicBackgroundLogs from './clinicDetails.jobs';
import AuditLogs from "../../AuditLogs/auditLogs";
import { filterDailyStatisticsForClinic } from '../../../redux/systemInfos/selectors';
import DailyStatistics from '../../../components/DailyStatistics/dailyStatistics';

const TABS_KEYS = ['general', 'users', 'healthRecords', 'backgroundJobs']

const { TabPane } = Tabs;

const { getClinicDetails, resetClinicBackgroundJobs, getClinicBackgroundJobs } = systemActions;

const DEFAULT_RECORDS_PER_PAGE = 20;
const DEFAULT_PAGE_SIZE_OPTIONS = [20, 30, 40, 50];
const INIT_PAGE = 1;

function ClinicDetails(props) {
  const [activeTab, setActiveTab] = useState('general');
  const [currentPage, setCurrentPage] = useState(INIT_PAGE);
  const [recordsPerPage, setRecordsPerPage] = useState(DEFAULT_RECORDS_PER_PAGE);
  const [jobLevel, setJobLevel] = useState(null);
  const [jobType, setJobType] = useState(null);
  const [jobsDate, setJobDate] = useState(null);

  useEffect(() => {
    const filters = {
      "limit": recordsPerPage,
      "page": currentPage,
      "level": jobLevel,
      "type": jobType,
      "date": jobsDate
    }
    props.getClinicBackgroundJobs(props.match.params.clinicId, filters);
    props.getClinicDetails(props.match.params.clinicId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match.params?.clinicId])

  useEffect(() => {
    const { selectedTab } = props.match.params;
    if (TABS_KEYS.includes(selectedTab)) setActiveTab(selectedTab);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match.params?.selectedTab])

  function handleOnChangeTab(key) {
    setActiveTab(key);
  }

  function updateTableData(pageData) {
    const { current, pageSize } = pageData;
    let newPageNumber = current;

    if (pageSize !== recordsPerPage) newPageNumber = INIT_PAGE;

    const filters = {
      "limit": pageSize,
      "page": newPageNumber,
      "level": jobLevel,
      "type": jobType,
      "date": jobsDate
    }
    props.getClinicBackgroundJobs(props.match.params.clinicId, filters);
    setCurrentPage(current);
    setRecordsPerPage(pageSize);
  }

  function submitFilters() {
    props.resetClinicBackgroundJobs();

    setCurrentPage(INIT_PAGE);
    const filters = {
      "limit": recordsPerPage,
      "page": INIT_PAGE,
      "level": jobLevel,
      "type": jobType,
      "date": jobsDate
    }
    props.getClinicBackgroundJobs(props.match.params.clinicId, filters);
  }

  function breadcrumbRenderFactory(selectedTab) {
    return (
      <Breadcrumb>
        <Breadcrumb.Item
          className={clinicsStyles.breadcrumbItem}
          onClick={() => props.history.push('/Dashboard/clinics')}
        >
          <IntlMessages id="general.clinics" />
        </Breadcrumb.Item>

        <Breadcrumb.Item onClick={() => setActiveTab('general')} className={clinicsStyles.breadcrumbItem}>
          {props.clinicDetails.title}
        </Breadcrumb.Item>

        <Breadcrumb.Item>
          <IntlMessages id={`general.${selectedTab}`} />
        </Breadcrumb.Item>
      </Breadcrumb>
    )
  }

  function filterDailyStatistics(statisticCategory, orderByCatecgory, sortingOrder, startDate, endDate) {
    return props.filterDailyStatistics(statisticCategory, orderByCatecgory, sortingOrder, startDate, endDate)
  }

  function handleRowOnClick(record) {
    props.history.push(`/Dashboard/userDetails/${record.id}`)
  }

  return (
    <Spin spinning={props.loading > 0}>
      <Box>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Tabs defaultActiveKey="general" onChange={handleOnChangeTab} activeKey={activeTab}>
              <TabPane tab={DvIntlMessages('general.general').toUpperCase()} key="general">
                {breadcrumbRenderFactory('general')}
                <ClinicDetailsGeneral />
              </TabPane>
              <TabPane tab={DvIntlMessages('general.users').toUpperCase()} key="users">
                {breadcrumbRenderFactory('users')}
                <ClinicDetailsUsers users={props.clinicDetails?.users} />
              </TabPane>
              <TabPane tab={DvIntlMessages('general.healthRecords').toUpperCase()} key="healthRecords">
                {breadcrumbRenderFactory('healthRecords')}
                <ClinicDetailsHealthRecords data={props.clinicDetails?.healthRecords} />
              </TabPane>
              <TabPane tab={DvIntlMessages('general.backgroundJob').toUpperCase()} key="backgroundJobs">
                {breadcrumbRenderFactory('backgroundJob')}
                <ClinicBackgroundLogs
                  pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
                  data={props.clinicBackgroundJobs}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  recordsPerPage={recordsPerPage}
                  setRecordsPerPage={setRecordsPerPage}
                  updateTableData={updateTableData}
                  setJobLevel={setJobLevel}
                  setJobType={setJobType}
                  setJobDate={setJobDate}
                  submitFilters={submitFilters}
                  numberOfLogs={props.numberOfLogs}
                />
              </TabPane>
              <TabPane tab={DvIntlMessages('general.auditLogs').toUpperCase()} key="auditLogs">
                {breadcrumbRenderFactory('auditLogs')}
                <AuditLogs />
              </TabPane>
              <TabPane tab={DvIntlMessages('general.statistics').toUpperCase()} key="statistics">
                {breadcrumbRenderFactory('statistics')}
                <DailyStatistics
                  filterDailyStatistics={filterDailyStatistics}
                  handleRowOnClick={handleRowOnClick} />
              </TabPane>
            </Tabs>
          </Col>
        </Row>
      </Box>
    </Spin>
  )
}

ClinicDetails.propTypes = {
  clinicDetails: PropTypes.object.isRequired,
  clinicBackgroundJobs: PropTypes.array.isRequired,
  loading: PropTypes.number.isRequired,
  numberOfLogs: PropTypes.number.isRequired,

  getClinicDetails: PropTypes.func.isRequired,
  getClinicBackgroundJobs: PropTypes.func.isRequired,
  resetClinicBackgroundJobs: PropTypes.func.isRequired,
  filterDailyStatistics: PropTypes.func.isRequired,
};

ClinicDetails.defaultProps = {
  clinicDetails: {},
  clinicBackgroundJobs: []
}

const mapActionsToProps = {
  getClinicDetails,
  resetClinicBackgroundJobs,
  getClinicBackgroundJobs
};

function mapStateToProps(state, props) {
  const clinicBackgroundJobs = state.SystemInfos.get('clinicBackgroundJobs');
  const clinicId = parseInt(props.match.params?.clinicId);
  return {
    clinicDetails: state.SystemInfos.get('clinicDetails')?.summaryData,
    clinicBackgroundJobs: clinicBackgroundJobs?.logs ?? [],
    numberOfLogs: clinicBackgroundJobs?.numberOfLogs,
    loading: state.SystemInfos.get('loading'),
    filterDailyStatistics: (statisticCategory, orderByCatecgory, sortingOrder, startDate, endDate) => filterDailyStatisticsForClinic(state, statisticCategory, orderByCatecgory, sortingOrder, clinicId, startDate, endDate),
  };
}

const wrappedClinicDetails = withRouter(ClinicDetails);
const connectedClinicDetails = connect(mapStateToProps, mapActionsToProps)(wrappedClinicDetails);
export default connectedClinicDetails;